import React, { useState, useMemo, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Navigation, Pagination } from 'swiper/modules';
import '../styles/CustomSwiper.css';
import { colour } from '../styles/GlobalStyles';
import { Chevron } from '../assets/icons';

function CustomSwiper(props) {
	const images = props.images;
	const title = props.title;
	const imageLimit = props.imageLimit;
	const arrowClicked = props.arrowClicked;
	const imageClick = props.imageClick;
	const [elementHover, setElementHover] = useState(false);
	const [swiperInstance, setSwiperInstance] = useState(null);
	// Active ORIGINAL slide. Under loop, activeIndex points at a clone, so we
	// always track realIndex instead.
	const [activeIndex, setActiveIndex] = useState(0);

	// Slide list Swiper will receive, with NO null holes: filter google-maps
	// URLs BEFORE imageLimit so the array index equals Swiper's slide index
	// (the ±1 window comparison relies on that alignment).
	const slideImages = useMemo(() => {
		if (images === null || images === undefined) {
			return [];
		}
		return images
			.filter((item) => item && item.includes("maps.googleapis.com") === false)
			.filter((item, index) => (index + 1) <= imageLimit);
	}, [images, imageLimit]);

	const slideCount = slideImages.length;
	// Enable Swiper's loop mode only when there are enough slides (>= 3).
	// Computed from the FILTERED count so it matches what Swiper renders.
	const enableLoop = slideCount >= 3;

	// A slide renders a real <img> only when it is the active slide or an
	// immediate neighbour. Circular distance avoids a placeholder flash at the
	// loop seam (slide 0 <-> slide N-1). Slides outside the window are empty
	// placeholders -> no decoded bitmap; slides swiped past revert to
	// placeholders on the next slideChange, so decoded-image memory never
	// accumulates across a long swipe session. This is the iOS-OOM fix: a feed
	// of ~150 unwindowed cards can no longer hold every slide's bitmap at once.
	const isWithinWindow = useCallback((index) => {
		if (slideCount === 0) {
			return false;
		}
		const linear = Math.abs(index - activeIndex);
		const distance = enableLoop ? Math.min(linear, slideCount - linear) : linear;
		return distance <= 1;
	}, [activeIndex, enableLoop, slideCount]);

	const showArrows = () => {
		if ( images === null ) {
			return;
		}
		else if ( images.length > 1 ) {
			setElementHover(true)
		}
	};

	const hideArrows = () => {
		setElementHover(false)
	};

	return (
		images !== undefined || swiperInstance !== null ?
			<div
				className={"custom-swiper-module-container " + (elementHover === true ? "custom-swiper-module-container-hover" : "")}
				onMouseEnter={() => showArrows()}
				onMouseLeave={() => hideArrows()}
			>
				<Swiper
					pagination={{
						dynamicBullets: true,
					}}
					onSwiper={(swiper) => {
						setSwiperInstance(swiper);
						// Seed the window from the real initial slide (normally 0).
						setActiveIndex(swiper.realIndex ?? 0);
					}}
					onSlideChange={(swiper) => {
						// realIndex is the ORIGINAL slide index even under loop,
						// where activeIndex would point at a clone.
						setActiveIndex(swiper.realIndex ?? 0);
					}}
					modules={[Pagination, Navigation]}
					className="custom-swiper-outer-container"
					loop={enableLoop}
					// slidesPerView={5}
					navigation={{
						prevEl: '.swiper-button-prev',
						nextEl: '.swiper-button-next',
					}}
				>
					<div className="swiper-button-prev" onClick={(e) => arrowClicked === null ? null : arrowClicked(e)}>
						<Chevron
							className="custom-swiper-icon"
							fill={colour.gray07}
						/>
					</div>
      				<div className="swiper-button-next" onClick={(e) => arrowClicked === null ? null : arrowClicked(e)}>
						<Chevron
							className="custom-swiper-icon"
							fill={colour.gray07}
						/>
					</div>
					{
						slideCount === 0 ?
						<div className="custom-swiper-image-container">

						</div>
						:
						slideImages.map((item, index) =>
							<SwiperSlide
								key={index}
							>
								<div
									className="custom-swiper-image-container"
									onClick={imageClick === null ? null : () => imageClick()}
								>
									{
										// Lazy windowing: only slides within ±1 of the
										// active slide mount a real <img>; the rest are
										// same-sized empty placeholders (the container
										// owns the height, so no layout jump).
										isWithinWindow(index) ?
										<img
											src={item}
											className="custom-swiper-image"
											alt={title}
											loading="lazy"
											decoding="async"
										/>
										:
										<div
											className="custom-swiper-image-placeholder"
											aria-hidden="true"
										/>
									}
								</div>
							</SwiperSlide>
						)
					}
				</Swiper>
			</div>
		:
		null
	)
}

export default CustomSwiper;
